import mapboxgl from 'mapbox-gl';

import {GeoMap, Layer, MapCtrlEventType} from '../../../ui/map';
import {AbstractObj, Obj, OBJ, SIGNAL, SLOT} from '../../../obj';
import {DRAW_MAP_LAYER_ID_PREFIX} from '../../../ui/map/draw/constants';
import {Color as LayerColor, DO_NOT_MAIL_PILL_IMAGE_ID, Id as LayerId, layers} from './layer';
import {Id as SourceId, PROJECT_SCOPED_PARCEL_TILES_ENABLED, parcelFilterSource, parcelFilterTileUrl, parcelTileUrl, sources} from './source';
import {bind} from '../../../util';
import {MapStyle, StyleControl} from '../../../ui/map/controls';
import {InteractiveMapControlMode, PARCEL_SOURCE_LAYER_ID} from '../../../constants';

interface IFeatureHoverInfo {
	id: number | string | undefined;
	source: string,
	sourceLayer: string | undefined;
}

interface IParcelFilterLayerState {
	enabled: boolean;
	fillLayerId: string;
	filterId: number;
	// True when this top-level filter is an exclude group (its `__filter_group__`
	// expression rhs carries `:exclude`). Exclude layers paint red, sit above the
	// include (purple) layers, and are what the editor's eyeball hides.
	isExclude: boolean;
	lineLayerId: string;
	revision: number;
	sourceId: string;
}

// Mirror of the backend `__filter_group__` sentinel + parse_filter_group_rhs
// (core/models.py) — the only client-side signal of include/exclude polarity.
const FILTER_GROUP_EXPRESSION_LHS = '__filter_group__';
const FILTER_GROUP_EXPRESSION_OPERATOR = 'exact';
const FILTER_GROUP_DIRECTION_EXCLUDE = 'exclude';

function isExcludeGroupFilter(filter: IFilter): boolean {
	const expr = filter.expression;
	if (!expr || expr.lhs !== FILTER_GROUP_EXPRESSION_LHS || expr.operator !== FILTER_GROUP_EXPRESSION_OPERATOR) {
		return false;
	}
	const direction = (expr.rhs || '').toLowerCase().split(':')[1] || '';
	return direction === FILTER_GROUP_DIRECTION_EXCLUDE;
}

@OBJ
export class ProjectDetailGeoMap extends GeoMap {
	// Session-only "eyeball" state — true => exclude (red) layers are hidden on
	// the map. Visual only; never touches filter enabled/counts. Re-applied on
	// every sync + style change so re-rendered exclude layers stay hidden.
	private excludeLayersHiddenFlag: boolean;
	private hoverInfo: IFeatureHoverInfo | null;
	private parcelFilterLayers: Map<number, IParcelFilterLayerState>;
	private parcelLayerExplicitlySetPks: Array<ParcelPk> | null;
	private parcelLayerExplicitlySetVisibility: boolean | null;
	private parcelTileRefreshTimeout: number | null;
	private parcelTileRevision: number;
	private slug: string;

	constructor(parent: AbstractObj, debug: boolean, slug: string) {
		super(parent, debug);
		this.excludeLayersHiddenFlag = false;
		this.hoverInfo = null;
		this.parcelFilterLayers = new Map<number, IParcelFilterLayerState>();
		this.parcelLayerExplicitlySetPks = null;
		this.parcelLayerExplicitlySetVisibility = null;
		this.parcelTileRefreshTimeout = null;
		this.parcelTileRevision = 0;
		this.slug = slug;
		Obj.connect(
			this, 'styleChanged',
			this, '_styleChanged');
	}

	destroy(): void {
		if (this.parcelTileRefreshTimeout !== null) {
			window.clearTimeout(this.parcelTileRefreshTimeout);
			this.parcelTileRefreshTimeout = null;
		}
		this.parcelLayerExplicitlySetPks = null;
		this.parcelLayerExplicitlySetVisibility = null;
		this.parcelFilterLayers.clear();
		super.destroy();
	}

	private filterLayerMouseEnterEvent(event: mapboxgl.MapMouseEvent): void {
		event.target.getCanvasContainer().classList.add('cursor--pointer');
	}

	private filterLayerMouseLeaveEvent(event: mapboxgl.MapMouseEvent): void {
		event.target.getCanvasContainer().classList.remove('cursor--pointer');
	}

	// Lane E — resolve the filter id of the polygon rendered at a screen point, from
	// the FilterFill layer's rendered feature (whose id IS the filter id, set by
	// filterToFeature). Works for NESTED leaves too — they render via the recursive
	// feature collection (PR #118) — unlike the top-level-only fetchFilters(point).
	// Used for both click-to-open and double-click-to-edit.
	filterIdAtPoint(x: number, y: number): number | null {
		if (!this.map) {
			return null;
		}
		const features = this.map.queryRenderedFeatures(
			[x, y],
			{layers: [LayerId.FilterFill]});
		for (const feature of features) {
			// The filter id lives in PROPERTIES (the source uses generateId:true, so
			// feature.id is an auto-generated id, not the filter id).
			const filterId = feature.properties ? feature.properties.filterId : undefined;
			if (typeof filterId === 'number') {
				return filterId;
			}
		}
		return null;
	}

	// Lane E — convert a map-canvas-relative point (mapbox event.point, as carried by
	// the mapClicked signal) into VIEWPORT coordinates, so a DOM popup anchored with
	// Menu.setAbsolutePosition lands under the cursor. The map canvas is offset from
	// the viewport by the workspace chrome (drawer + header), so the raw point would
	// mis-place the popup.
	viewportPointFromMapPoint(x: number, y: number): {x: number; y: number;} {
		if (!this.map) {
			return {x, y};
		}
		const rect = this.map.getContainer().getBoundingClientRect();
		return {x: x + rect.left, y: y + rect.top};
	}

	filterParcels(pks: Array<ParcelPk> | null): void {
		if (this._filterParcels(pks)) {
			this.parcelLayerExplicitlySetPks = pks;
		}
	}

	refreshProjectScopedParcelTiles(): void {
		if (!this.usesProjectScopedParcelTiles()) {
			return;
		}
		if (this.parcelTileRefreshTimeout !== null) {
			window.clearTimeout(this.parcelTileRefreshTimeout);
		}
		this.parcelTileRefreshTimeout = window.setTimeout(() => {
			this.parcelTileRefreshTimeout = null;
			this.parcelTileRevision += 1;
			this.setProjectScopedParcelTileUrl();
		}, 50);
	}

	// The fill color for a per-filter layer: red for exclude groups, else the
	// theme's include purple. Single source of truth for both initial paint and
	// the style-change recolor (_setLayerColor).
	private parcelFilterFillColor(state: IParcelFilterLayerState, light: boolean): string {
		if (state.isExclude) {
			return LayerColor.ExcludeFillDark;
		}
		return light ? LayerColor.ParcelFillLight : LayerColor.ParcelFillDark;
	}

	private parcelFilterFillLayer(state: IParcelFilterLayerState): Layer {
		const base = <Layer>layers.find(layer => (layer.id === LayerId.ParcelFill));
		// NB: fill-color is applied imperatively after addLayer (see
		// addParcelFilterLayers) — overriding `paint` in this literal breaks the
		// Layer discriminated-union narrowing.
		return {
			...base,
			id: state.fillLayerId,
			layout: {
				...(base.layout || {}),
				visibility: this.parcelFilterLayerVisibilityValue(state),
			},
			source: state.sourceId,
			'source-layer': PARCEL_SOURCE_LAYER_ID,
		};
	}

	private parcelFilterLineLayer(state: IParcelFilterLayerState): Layer {
		const base = <Layer>layers.find(layer => (layer.id === LayerId.ParcelLine));
		return {
			...base,
			id: state.lineLayerId,
			layout: {
				...(base.layout || {}),
				visibility: this.parcelFilterLayerVisibilityValue(state),
			},
			source: state.sourceId,
			'source-layer': PARCEL_SOURCE_LAYER_ID,
		};
	}

	private parcelFilterState(filter: IFilter): IParcelFilterLayerState {
		return {
			enabled: filter.enabled,
			fillLayerId: `id_lb-parcel-fill-layer-filter-${filter.id}`,
			filterId: filter.id,
			isExclude: isExcludeGroupFilter(filter),
			lineLayerId: `id_lb-parcel-line-layer-filter-${filter.id}`,
			revision: 0,
			sourceId: `id_lb-parcel-source-filter-${filter.id}`,
		};
	}

	// The visibility a per-filter layer should currently have: hidden when the
	// filter is disabled OR (it's an exclude layer AND the eyeball has hidden the
	// exclude group). `allowVisible=false` forces hidden regardless (used by the
	// global parcel hide).
	private parcelFilterLayerVisibilityValue(state: IParcelFilterLayerState, allowVisible: boolean = true): 'visible' | 'none' {
		if (!allowVisible || !state.enabled) {
			return 'none';
		}
		if (state.isExclude && this.excludeLayersHiddenFlag) {
			return 'none';
		}
		return 'visible';
	}

	private setParcelFilterLayerVisibility(state: IParcelFilterLayerState, allowVisible: boolean = true): void {
		if (this.map) {
			const visibility = this.parcelFilterLayerVisibilityValue(state, allowVisible);
			if (this.map.getLayer(state.fillLayerId)) {
				this.map.setLayoutProperty(state.fillLayerId, 'visibility', visibility);
			}
			if (this.map.getLayer(state.lineLayerId)) {
				this.map.setLayoutProperty(state.lineLayerId, 'visibility', visibility);
			}
		}
	}

	private setParcelFilterTileUrl(state: IParcelFilterLayerState): void {
		if (this.map) {
			const source = <mapboxgl.AnySourceImpl & {setTiles?: (tiles: ReadonlyArray<string>) => unknown}>this.map.getSource(state.sourceId);
			if (source && typeof source.setTiles === 'function') {
				source.setTiles([parcelFilterTileUrl(this.slug, state.filterId, state.revision)]);
			}
		}
	}

	private addParcelFilterLayers(state: IParcelFilterLayerState): void {
		this.addSource(state.sourceId, parcelFilterSource(this.slug, state.filterId, state.revision));
		this.addLayer(this.parcelFilterFillLayer(state), LayerId.DoNotMailParcelCentroid);
		this.addLayer(this.parcelFilterLineLayer(state), LayerId.DoNotMailParcelCentroid);
		if (this.map && this.map.getLayer(state.fillLayerId)) {
			// Exclude → red, include → theme purple. Set here (not in the layer
			// literal) to keep the Layer union's type narrowing intact.
			this.map.setPaintProperty(
				state.fillLayerId,
				'fill-color',
				this.parcelFilterFillColor(state, this._currentStyleHasDarkBackground()));
		}
		if (this.map) {
			this.map.on(
				MapCtrlEventType.MouseMove,
				state.fillLayerId,
				this.layerMouseMoveEvent);
			this.map.on(
				MapCtrlEventType.MouseLeave,
				state.fillLayerId,
				this.layerMouseLeaveEvent);
		}
	}

	private removeParcelFilterLayers(state: IParcelFilterLayerState): void {
		if (this.map) {
			this.map.off(
				MapCtrlEventType.MouseMove,
				state.fillLayerId,
				this.layerMouseMoveEvent);
			this.map.off(
				MapCtrlEventType.MouseLeave,
				state.fillLayerId,
				this.layerMouseLeaveEvent);
		}
		this.removeLayer(state.fillLayerId);
		this.removeLayer(state.lineLayerId);
		this.removeSource(state.sourceId);
	}

	private upsertParcelFilterLayer(filter: IFilter, refresh: boolean): void {
		if (filter.parentId !== null) {
			return;
		}
		let state = this.parcelFilterLayers.get(filter.id);
		if (!state) {
			state = this.parcelFilterState(filter);
			this.parcelFilterLayers.set(filter.id, state);
			this.addParcelFilterLayers(state);
		}
		state.enabled = filter.enabled;
		const nowExclude = isExcludeGroupFilter(filter);
		if (nowExclude !== state.isExclude) {
			// Polarity flipped (e.g. an include group re-saved as exclude) — recolor
			// the live fill so the layer matches its new direction.
			state.isExclude = nowExclude;
			if (this.map && this.map.getLayer(state.fillLayerId)) {
				this.map.setPaintProperty(
					state.fillLayerId,
					'fill-color',
					this.parcelFilterFillColor(state, this._currentStyleHasDarkBackground()));
			}
		}
		this.setParcelFilterLayerVisibility(state);
		if (refresh && state.enabled) {
			state.revision += 1;
			this.setParcelFilterTileUrl(state);
		}
	}

	syncParcelFilterTiles(filters: Array<IFilter>, opt?: {removeMissing?: boolean; refreshFilterIds?: Array<number>}): void {
		const topLevelFilters = filters.filter(filter => (filter.parentId === null));
		const nextIds = new Set<number>(topLevelFilters.map(filter => filter.id));
		if (opt && opt.removeMissing) {
			for (const [filterId, state] of this.parcelFilterLayers.entries()) {
				if (!nextIds.has(filterId)) {
					this.removeParcelFilterLayers(state);
					this.parcelFilterLayers.delete(filterId);
				}
			}
		}
		const refreshFilterIds = new Set<number>((opt && opt.refreshFilterIds) || []);
		for (const filter of topLevelFilters) {
			this.upsertParcelFilterLayer(filter, refreshFilterIds.has(filter.id));
		}
		this.raiseExcludeFilterLayers();
	}

	// Keep the exclude (red) layers stacked just beneath the DNM layers — i.e.
	// above every include (purple) layer — so where an include set and the
	// exclude set overlap, the excluded reading wins visually. Idempotent;
	// re-run after every sync. (True subtraction of the include tiles is a
	// deferred v2-resolver change; see filter_exclude_map_visualization.md.)
	private raiseExcludeFilterLayers(): void {
		if (!this.map) {
			return;
		}
		for (const state of this.parcelFilterLayers.values()) {
			if (!state.isExclude) {
				continue;
			}
			if (this.map.getLayer(state.fillLayerId)) {
				this.map.moveLayer(state.fillLayerId, LayerId.DoNotMailParcelFill);
			}
			if (this.map.getLayer(state.lineLayerId)) {
				this.map.moveLayer(state.lineLayerId, LayerId.DoNotMailParcelFill);
			}
		}
	}

	// Eyeball (editor Exclude-zone header): hide/show all exclude (red) layers on
	// the map. Visual only — never touches filter `enabled` / counts / totals.
	// Session-only state, re-applied on sync + style change.
	setExcludeGroupHidden(hidden: boolean): void {
		this.excludeLayersHiddenFlag = hidden;
		for (const state of this.parcelFilterLayers.values()) {
			if (state.isExclude) {
				this.setParcelFilterLayerVisibility(state);
			}
		}
	}

	excludeGroupHidden(): boolean {
		return this.excludeLayersHiddenFlag;
	}

	private setProjectScopedParcelTileUrl(): void {
		if (this.map && this.usesProjectScopedParcelTiles()) {
			const source = <mapboxgl.AnySourceImpl & {setTiles?: (tiles: ReadonlyArray<string>) => unknown}>this.map.getSource(SourceId.Parcel);
			if (source && typeof source.setTiles === 'function') {
				source.setTiles([parcelTileUrl(this.slug, this.parcelTileRevision)]);
			}
		}
	}

	usesProjectScopedParcelTiles(): boolean {
		return PROJECT_SCOPED_PARCEL_TILES_ENABLED && this.slug.length > 0;
	}

	private _filterParcels(pks: Array<ParcelPk> | null): boolean {
		if (this.map) {
			const filter = Array.isArray(pks) ?
				[
					'in',
					[
						'get',
						'area_pk',
					],
					[
						'literal',
						pks,
					],
				] :
				null;
			this.map.setFilter(LayerId.ParcelFill, filter);
			this.map.setFilter(LayerId.ParcelLine, filter);
			return true;
		}
		return false;
	}

	private isLayerVisible(layerId: string): boolean {
		return this.layerVisibility(layerId) !== 'none';
	}

	isParcelsVisible(): boolean {
		for (const state of this.parcelFilterLayers.values()) {
			if (this.isLayerVisible(state.fillLayerId)) {
				return true;
			}
		}
		return this.isLayerVisible(LayerId.ParcelFill);
	}

	private layerVisibility(layerId: string): string {
		return this.map ?
			this.map.getLayoutProperty(layerId, 'visibility') || '' :
			'';
	}

	protected mapLoadEvent(event: mapboxgl.MapboxEvent): void {
		const projectSources = sources(this.slug, this.parcelTileRevision);
		for (let i = 0; i < projectSources.length; ++i) {
			this.addSource(projectSources[i].id, projectSources[i].source);
		}
		// Register the "DNM" pill icon BEFORE adding layers — the low-zoom DNM
		// centroid symbol layer references it by id.
		this._ensureDoNotMailPillImage();
		for (let i = 0; i < layers.length; ++i) {
			this.addLayer(layers[i]);
		}
		// Called here so above layers, sources may be defined before super
		// class attempts to load any pending source data.
		super.mapLoadEvent(event);
		this._setLayerColor(this._currentStyleHasDarkBackground());
		event.target.on(
			MapCtrlEventType.MouseMove,
			LayerId.DoNotMailParcelFill,
			this.layerMouseMoveEvent);
		event.target.on(
			MapCtrlEventType.MouseLeave,
			LayerId.DoNotMailParcelFill,
			this.layerMouseLeaveEvent);
		event.target.on(
			MapCtrlEventType.MouseMove,
			LayerId.ParcelFill,
			this.layerMouseMoveEvent);
		event.target.on(
			MapCtrlEventType.MouseLeave,
			LayerId.ParcelFill,
			this.layerMouseLeaveEvent);
		event.target.on(
			MapCtrlEventType.MouseEnter,
			LayerId.FilterFill,
			this.filterLayerMouseEnterEvent);
		event.target.on(
			MapCtrlEventType.MouseLeave,
			LayerId.FilterFill,
			this.filterLayerMouseLeaveEvent);
	}

	// Hover quick-info: fired as the pointer moves over a parcel (with the
	// area_pk + geo coord + viewport cursor position) and when it leaves
	// all parcels. The view debounces a parcel fetch on dwell and shows a
	// follow-the-cursor tooltip. Tiles carry only area_pk, so the info
	// itself comes from the view's fetch.
	@SIGNAL
	parcelHovered(areaPk: number | string, lng: number, lat: number, clientX: number, clientY: number): void {
	}

	@SIGNAL
	parcelHoverEnded(): void {
	}

	@bind
	private layerMouseLeaveEvent(event: mapboxgl.MapMouseEvent): void {
		if (this.hoverInfo) {
			event.target.setFeatureState(
				{
					id: this.hoverInfo.id,
					source: this.hoverInfo.source,
					sourceLayer: this.hoverInfo.sourceLayer,
				},
				{
					hover: false,
				});
			this.hoverInfo = null;
		}
		this.parcelHoverEnded();
	}

	@bind
	private layerMouseMoveEvent(event: mapboxgl.MapLayerMouseEvent): void {
		if (event.features && event.features.length > 0) {
			if (this.hoverInfo) {
				event.target.setFeatureState(
					{
						id: this.hoverInfo.id,
						source: this.hoverInfo.source,
						sourceLayer: this.hoverInfo.sourceLayer,
					},
					{
						hover: false,
					});
			}
			this.hoverInfo = {
				id: event.features[0].id,
				source: event.features[0].source,
				sourceLayer: event.features[0].sourceLayer,
			};
			event.target.setFeatureState(
				{
					id: this.hoverInfo.id,
					source: this.hoverInfo.source,
					sourceLayer: this.hoverInfo.sourceLayer,
				},
				{
					hover: true,
				});
			const areaPk = event.features[0].id;
			if (areaPk !== undefined) {
				const oe = event.originalEvent;
				this.parcelHovered(
					areaPk,
					event.lngLat.lng,
					event.lngLat.lat,
					oe ? oe.clientX : event.point.x,
					oe ? oe.clientY : event.point.y);
			}
		}
	}

	setDoNotMailSourceData(data: {fill: GeoJsonFeature | GeoJsonFeatureCollection, point: GeoJsonFeature | GeoJsonFeatureCollection}): void {
		this.setSourceData(SourceId.DoNotMailParcel, data.fill);
		this.setSourceData(SourceId.DoNotMailParcelCentroid, data.point);
	}

	setFilterSourceData(data: GeoJsonFeature | GeoJsonFeatureCollection): void {
		this.setSourceData(SourceId.Filter, data);
	}

	private _setLayerColor(light: boolean): void {
		if (this.map) {
			const color = light ? LayerColor.ParcelFillLight : LayerColor.ParcelFillDark;
			this.map.setPaintProperty(
				LayerId.ParcelFill,
				'fill-color',
				color);
			for (const state of this.parcelFilterLayers.values()) {
				if (this.map.getLayer(state.fillLayerId)) {
					// Exclude layers stay red across theme swaps; include layers track
					// the theme purple.
					this.map.setPaintProperty(
						state.fillLayerId,
						'fill-color',
						this.parcelFilterFillColor(state, light));
				}
			}
		}
	}

	setParcelsVisible(visible: boolean): void {
		if (this.map) {
			const v = (visible && !this.usesProjectScopedParcelTiles()) ? 'visible' : 'none';
			this.map.setLayoutProperty(LayerId.ParcelFill, 'visibility', v);
			this.map.setLayoutProperty(LayerId.ParcelLine, 'visibility', v);
			for (const state of this.parcelFilterLayers.values()) {
				this.setParcelFilterLayerVisibility(state, visible);
			}
			this.parcelLayerExplicitlySetVisibility = visible;
		}
	}

	@SLOT
	private _styleChanged(newStyleUrl: string): void {
		// A style swap clears custom images — re-register the DNM pill icon.
		this._ensureDoNotMailPillImage();
		this._setLayerColor(this._currentStyleHasDarkBackground());
		this.setProjectScopedParcelTileUrl();
		for (const state of this.parcelFilterLayers.values()) {
			this.setParcelFilterTileUrl(state);
			this.setParcelFilterLayerVisibility(state);
		}
		this.raiseExcludeFilterLayers();
		this._filterParcels(this.parcelLayerExplicitlySetPks);
		if (this.parcelLayerExplicitlySetVisibility === true) {
			this.setParcelsVisible(true);
		}
	}

	// Build + register the "DNM" pill marker image: a dark-grey rounded badge
	// with white all-caps "DNM", styled to echo the chip-strip count sub-pill
	// (border-radius pill, 600 weight, ~10.5px). Drawn to a canvas at 2x for
	// crispness and added once per style. Guarded so a re-add is a no-op.
	private _ensureDoNotMailPillImage(): void {
		if (!this.map || this.map.hasImage(DO_NOT_MAIL_PILL_IMAGE_ID)) {
			return;
		}
		const ratio = 2;
		const text = 'DNM';
		const fontPx = 11;
		const padX = 7;
		const padY = 3;
		const font = `600 ${fontPx}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`;
		const measureCanvas = document.createElement('canvas');
		const measureCtx = measureCanvas.getContext('2d');
		if (!measureCtx) {
			return;
		}
		measureCtx.font = font;
		const textW = Math.ceil(measureCtx.measureText(text).width);
		const cssW = textW + padX * 2;
		const cssH = fontPx + padY * 2;
		const w = cssW * ratio;
		const h = cssH * ratio;
		const canvas = document.createElement('canvas');
		canvas.width = w;
		canvas.height = h;
		const ctx = canvas.getContext('2d');
		if (!ctx) {
			return;
		}
		ctx.scale(ratio, ratio);
		const radius = cssH / 2;
		ctx.beginPath();
		ctx.moveTo(radius, 0);
		ctx.arcTo(cssW, 0, cssW, cssH, radius);
		ctx.arcTo(cssW, cssH, 0, cssH, radius);
		ctx.arcTo(0, cssH, 0, 0, radius);
		ctx.arcTo(0, 0, cssW, 0, radius);
		ctx.closePath();
		ctx.fillStyle = LayerColor.DoNotMailFillDark;
		ctx.fill();
		ctx.fillStyle = LayerColor.DoNotMailPillText;
		ctx.font = font;
		ctx.textAlign = 'center';
		ctx.textBaseline = 'middle';
		ctx.fillText(text, cssW / 2, cssH / 2 + 0.5);
		const imageData = ctx.getImageData(0, 0, w, h);
		this.map.addImage(DO_NOT_MAIL_PILL_IMAGE_ID, imageData, {pixelRatio: ratio});
	}

	private _currentStyleHasDarkBackground(): boolean {
		const ctrl = <StyleControl | null>this.controlForMode(
			InteractiveMapControlMode.StylePickerMode);
		const styleUrl = ctrl ?
			ctrl.styleUrl() :
			'';
		return styleUrl === StyleControl.styleUrlForMapStyle(MapStyle.Satellite);
	}
}

function isDrawLayerId(layerId: string): boolean {
	return layerId.startsWith(DRAW_MAP_LAYER_ID_PREFIX);
}
